import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { AutocompleteProps } from '@mui/material';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { Validator, useChoicesContext, useInput, FieldTitle, InputHelperText } from 'react-admin';
import { useFormContext, useFormState } from 'react-hook-form';
import { Autocomplete } from 'shared/mui/Autocomplete/Autocomplete';
import { Box } from 'shared/mui/Box';
import { TextField } from 'shared/mui/TextField/TextField';
import { MethodLogo } from 'types/embedded/methodLogo';

export type AutocompleteMultiselectInputProps<
  OptionType = MethodLogo,
  DisableClearable extends boolean | undefined = false,
  SupportCreate extends boolean | undefined = false,
> = Omit<
  AutocompleteProps<OptionType, true, DisableClearable, SupportCreate>,
  'renderInput' | 'options' | 'renderOption' | 'defaultValue'
> & {
  defaultValue?: MethodLogo;
  name: string;
  label: string;
  optionValue?: keyof MethodLogo;
  validate?: Validator | Validator[];
  error?: boolean;
  disabled?: boolean;
  newLogo?: string | null;
  onChangeLogo: Dispatch<SetStateAction<string | null | undefined>>;
};

const defaultSx = {
  '& .MuiInputBase-input': {
    minWidth: '0!important',
  },
};

export const MethodsLogoAutocompleteInput: React.FC<AutocompleteMultiselectInputProps> = ({
  name,
  optionValue = 'id',
  label,
  defaultValue,
  disabled,
  sx,
  onChangeLogo,
  validate,
  ...restProps
}) => {
  const {
    field,
    fieldState: { error, invalid },
  } = useInput({
    name,
    source: name,
    validate,
    ...restProps,
    defaultValue,
  });

  const { resetField } = useFormContext();
  const { defaultValues } = useFormState();
  const { allChoices, setFilters, displayedFilters } = useChoicesContext<MethodLogo>();
  const [inputValue, setInputValue] = useState('');

  const isInitialValue = (value?: MethodLogo | null) => {
    const initialValue = get(defaultValues, name) as MethodLogo | undefined;

    return Boolean(value?.[optionValue]) && value?.[optionValue] === initialValue?.[optionValue];
  };

  useEffect(() => {
    if (defaultValue) {
      if (field.value?.[optionValue] === defaultValue[optionValue]) {
        resetField(name, { defaultValue });
      } else {
        field.onChange(defaultValue);
      }
      setInputValue(defaultValue?.name || '');
    }
  }, [defaultValue]);

  const handleInputChange = (event: any, newInputValue: string) => {
    if (!event) {
      return;
    }
    setInputValue(newInputValue);
    const debouncedSetFilters = debounce(() => {
      setFilters({ name: newInputValue }, displayedFilters);
    }, 500);
    debouncedSetFilters();
  };

  const handleBlur = () => {
    setFilters({}, displayedFilters);
  };

  return (
    <Autocomplete
      {...field}
      {...restProps}
      // @ts-ignore
      defaultValue={defaultValue}
      disabled={disabled}
      getOptionLabel={(option) => {
        return option?.name ?? '';
      }}
      id={name}
      inputValue={inputValue}
      // @ts-ignore
      isOptionEqualToValue={(option, value) => {
        return option[optionValue] === value[optionValue];
      }}
      onBlur={handleBlur}
      onChange={(e, data) => {
        // @ts-ignore
        onChangeLogo(data?.filePath);

        // @ts-ignore
        if (isInitialValue(data)) {
          resetField(name);
          return;
        }

        return field.onChange(data);
      }}
      onInputChange={handleInputChange}
      options={allChoices || []}
      renderInput={(params) => (
        <TextField
          {...params}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
          error={invalid}
          helperText={<InputHelperText error={error?.message} />}
          label={<FieldTitle isRequired={(validate as any)?.isRequired} label={label} />}
          size="small"
          variant="outlined"
        />
      )}
      renderOption={(props, { name, filePath }) => (
        <Box component="li" display="flex" {...props}>
          <img
            alt={name}
            src={filePath}
            style={{ width: '80px', aspectRatio: '1', objectFit: 'contain', marginRight: 8 }}
          />
          <Box component="p">{name}</Box>
        </Box>
      )}
      sx={{ ...defaultSx, ...sx }}
      value={field.value}
    />
  );
};
